import BulkDelete from './recordm.actions.instance.bulkdelete.js';
import DeleteInstance from './recordm.actions.instance.delete.js';
import ExportInstances from './recordm.actions.instance.export.js';
import UriBuilder from '../helpers/module.uribuilder.js';

/**
 * suported opts:
 * `disableDelete` - disables both batch and instance specific delete actions
 * `disableGroupDelete` - disables this batch action even if the user can delete some instance. Defaults to false.
 * `disableGroupEdit` - disables this batch action even if the user can edit some instance. Defaults to false.
 * `duplicateInstanceOverride` - override function for the duplicate-instance action
 * `groupEditOverride` - override function for the group-edit action
 * */
const InstanceActions = function(core, opts) {
    opts = opts || {};

    var _batchActions = [
        {
            key: "export",
            label: core.translateString("recordm.search", "export.title"),
            isAllowed: function() {
                return true;
            },
            execute: function(definitionId, indexedInstancesM, ctx) {
                var ids = [];

                if (indexedInstancesM.length > 0) {

                    ids = indexedInstancesM.map(function(indexedM) {
                        return indexedM.getInstanceId();
                    });
                }

                new ExportInstances(core)
                    .execute({definitionId: definitionId, "ids": ids}, ctx);
            },
            executeOnQuery: function(definitionId, query, ctx) {
                new ExportInstances(core)
                    .execute({definitionId: definitionId, "query": query}, ctx);
            }
        },
        {
            key: "group-edit",
            label: core.translateString("recordm.instances", "action.group-edit"),
            isAllowed: function(definitionM, indexedInstanceM) {
                if (opts.disableGroupEdit) return false
                return definitionM.canCreateBulkRequests() &&
                       indexedInstanceM.canUpdate();
            },
            execute: function(definitionId, indexedInstancesM) {
                var ids = [];

                if (indexedInstancesM.length > 0) {

                    ids = indexedInstancesM.filter(function(indexedM) {
                        return indexedM.canUpdate();

                    }).map(function(indexedM) {
                            return indexedM.getInstanceId();
                        });
                }
                if(typeof opts.groupEditOverride == 'function') {
                    opts.groupEditOverride(definitionId, ids.join(','), undefined);
                }else{
                    core.navigateTo(UriBuilder.buildUriForGroupEditOnDataset(definitionId, ids.join(',')));
                }
            },
            executeOnQuery: function(definitionId, query) {
                if(typeof opts.groupEditOverride == 'function') {
                    opts.groupEditOverride(definitionId, undefined, query);
                }else {
                    core.navigateTo(UriBuilder.buildUriForGroupEditOnQuery(definitionId, query));
                }
            }
        },
        {
            key: "group-delete",
            label: core.translateString("recordm.instances", "action.group-delete"),
            icon: "icon-trash",
            isAllowed: function(definitionM, indexedInstanceM) {
                if (opts.disableGroupDelete) return false
                return definitionM.canCreateBulkRequests() &&
                       indexedInstanceM.canDelete();
            },
            execute: function(definitionId, indexedInstancesM) {
                var ids = [];

                if (indexedInstancesM.length > 0) {

                    ids = indexedInstancesM.filter(function(indexedM) {
                        return indexedM.canDelete();

                    }).map(function(indexedM) {
                       return indexedM.getInstanceId();
                    });
                }

                new BulkDelete(core)
                    .execute({definitionId: definitionId, "ids": ids});
            },
            executeOnQuery: function(definitionId, query) {
                new BulkDelete(core)
                    .execute({definitionId: definitionId, "query": query});
            }
        }
    ];

    var _instanceActions = [
        {
            key: "delete-instance",
            label: core.translateString("recordm.instances", "action.delete"),
            icon: "icon-trash",
            isAllowed: function(indexedInstanceM) {
                return indexedInstanceM.canDelete();
            },
            execute: function(indexedInstanceM) {
                new DeleteInstance(core)
                    .execute(indexedInstanceM);
            }
        },
        {
            key: "duplicate-instance",
            label: core.translateString("recordm.instances", "action.duplicate"),
            icon: "icon-copy",
            isAllowed: function(indexedInstanceM) {
                return indexedInstanceM.canDuplicateInstance();
            },
            execute: function(indexedInstanceM) {
                if(typeof opts.duplicateInstanceOverride == 'function') {
                    opts.duplicateInstanceOverride(indexedInstanceM.getInstanceId());
                }else{
                    core.navigateTo(UriBuilder.buildUriForDuplicateInstance(indexedInstanceM.getInstanceId()));
                }
            }
        }
    ];

    var _getAllowedInstanceActions = function(indexedInstanceM) {
        return _instanceActions.filter(function(action) {
            if(action.key === "delete-instance" && opts.disableDelete) return false;

            return action.isAllowed(indexedInstanceM);
        });
    };

    const _getAllowedBatchActions = function(definitionM, indexedInstancesM) {
        const allowedBatchActions = [];

        _batchActions.forEach((action) => {
            if(action.key !== "group-delete" || !opts.disableDelete){
                const firstInstanceM = indexedInstancesM.find((instanceM) => action.isAllowed(definitionM, instanceM));

                // If ate least one is allowed, add the action
                if(firstInstanceM !== undefined){
                    allowedBatchActions.push(action);
                }
            }
        });

        return allowedBatchActions;
    };

    const _getAllowedCustomBatchActions = function(definitionM, indexedInstancesM, ctx) {
        const allowedBatchActions = [];

        core.getCustomBatchActions().forEach((action) => {
            const firstInstanceM = indexedInstancesM.find((instanceM) => action.isAllowed(definitionM, instanceM, ctx));

            // If ate least one is allowed, add the action
            if(firstInstanceM !== undefined){
                allowedBatchActions.push(action);
            }
        });

        return allowedBatchActions;
    };

    var _getAction = function(actionKey, list) {
        var action = undefined;

        list.some(function(act) {
            if (act.key === actionKey) {
                action = act;
                return true;
            }
            return false;
        });

        return action;
    };

    return {
        getInstanceActions: _getAllowedInstanceActions,
        getBatchActions: _getAllowedBatchActions,
        getCustomBatchActions: _getAllowedCustomBatchActions,

        executeInstanceAction: function(actionKey, indexedInstanceM) {
            const action = _getAction(actionKey, _instanceActions);

            if (action !== undefined) {
                action.execute(indexedInstanceM);
            }
        },

        executeBatchActionOnDataset: function(bActionKey, definitionId, indexedInstancesM, ctx) {
            const batchAction = _getAction(bActionKey, _batchActions.concat(core.getCustomBatchActions()));

            if (batchAction !== undefined) {
                batchAction.execute(definitionId, indexedInstancesM, ctx);
            }
        },

        executeBatchActionOnQuery: function(bActionKey, definitionId, query, ctx) {
            const batchAction = _getAction(bActionKey, _batchActions.concat(core.getCustomBatchActions()));

            if (batchAction !== undefined) {
                batchAction.executeOnQuery(definitionId, query, ctx);
            }
        }
    }
};

InstanceActions.validateBatchAction = function (batchAction) {
    const errors = [];
    const actionType = typeof batchAction;

    if(actionType !== "object") {
        errors.push(`The action should be an 'object', not '${actionType}'`);
    } else if(Array.isArray(batchAction)) {
        // Arrays are also typed as objects, so we must check this
        errors.push(`The action should be an 'object', not 'Array'`);
    } else {
        ['key', 'label'].forEach(function (fieldName) {
            const fieldType = typeof batchAction[fieldName];
            if(fieldType !== 'string'){
                errors.push(`The action field '${fieldName}' should be a 'string', not '${fieldType}'`);
            }
        });

        ['isAllowed', 'execute', 'executeOnQuery'].forEach(function (methodName) {
            const fnType = typeof batchAction[methodName];
            if(fnType !== 'function'){
                errors.push(`The action field '${methodName}' should be a 'function', not '${fnType}'`);
            }
        });
    }

    if(errors.length > 0){
        window.console.error("Errors validating a Batch Action", batchAction, errors);
    }

    return errors;
};

export default InstanceActions;
